import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import MultiSelect from '../Common/MultiSelect';

const INDIAN_STATES = [
    "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh",
    "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana",
    "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep",
    "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
    "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const UserDrawer = ({ isOpen, onClose, user, onSave, loading }) => {
    // If user is passed, we are in Edit mode. If null, we are in Create mode.
    const isEditMode = !!user;

    // Initial State for Form
    const initialFormState = {
        name: '',
        email: '',
        password: '',
        role: 'client',
        phone: '',
        address: '',
        company: '',
        service_states: []
    };

    const [formData, setFormData] = useState(initialFormState);
    const [passwordChanged, setPasswordChanged] = useState(false);

    // --- Animation state (same pattern as Modal.js used by Create Order) ---
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const closeTimerRef = useRef(null);
    const openTimerRef = useRef(null);

    useEffect(() => {
        // Clear any existing timers
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        if (openTimerRef.current) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }

        if (isOpen) {
            // Opening: render first, then animate in
            setShouldRender(true);
            openTimerRef.current = setTimeout(() => {
                setIsVisible(true);
            }, 50);
        } else if (shouldRender) {
            // Closing: animate out first, then unmount
            setIsVisible(false);
            closeTimerRef.current = setTimeout(() => {
                setShouldRender(false);
            }, 450); // Wait for animation to complete
        }

        return () => {
            if (openTimerRef.current) clearTimeout(openTimerRef.current);
            // Don't clear closeTimer on cleanup - let the animation finish
        };
    }, [isOpen]);

    // Load user data when opening in Edit mode
    useEffect(() => {
        if (isOpen) {
            if (user) {
                setFormData({
                    name: user.name || '',
                    email: user.email || '',
                    password: '', // Don't populate password
                    role: user.role || 'client',
                    phone: user.phone || '',
                    address: user.address || '',
                    company: user.company || '',
                    service_states: user.service_states || []
                });
                setPasswordChanged(false);
            } else {
                setFormData(initialFormState);
                setPasswordChanged(false);
            }
        }
    }, [isOpen, user]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData, isEditMode, passwordChanged);
    };

    // Don't render anything until mount is requested
    if (!shouldRender) return null;

    // Use React Portal to render the drawer outside the current DOM hierarchy
    // This ensures it breaks out of any parent stacking contexts (like the dashboard layout)
    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999]">
            {/* Backdrop with slow fade — same as Create Order modal */}
            <div
                className={`fixed inset-0 bg-black transition-all duration-[400ms] ease-out ${isVisible ? 'bg-opacity-20' : 'bg-opacity-0'
                    }`}
                onClick={onClose}
            />

            {/* Sliding Panel with fade + slide — same as Create Order modal */}
            <div
                className={`fixed right-0 top-[64px] h-[calc(100vh-88px)] w-full max-w-md bg-white shadow-2xl
                    transition-all duration-[400ms] ease-out
                    ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
                    flex flex-col rounded-l-xl overflow-hidden`}
            >
                {/* Header - Fixed */}
                <div className="flex-shrink-0 px-6 py-4 bg-indigo-700">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="p-1.5 -ml-1 text-indigo-200 hover:text-white hover:bg-indigo-600 rounded-full transition-all focus:outline-none"
                            aria-label="Back"
                            title="Close"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <h2 className="text-lg font-medium text-white">
                            {isEditMode ? 'Edit User' : 'Create New User'}
                        </h2>
                    </div>
                    <div className="mt-1">
                        <p className="text-sm text-indigo-300">
                            {isEditMode
                                ? 'Update user details and permissions.'
                                : 'Fill in the information below to create a new user account.'}
                        </p>
                    </div>
                </div>

                {/* Form Body - Scrollable */}
                <div className="flex-1 px-4 sm:px-6 py-6 overflow-y-auto">
                    <form id="user-form" onSubmit={handleSubmit} className="space-y-6">

                        {/* Role Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                disabled={isEditMode}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                            >
                                <option value="client">Client (Customer)</option>
                                <option value="vendor">Vendor (Partner)</option>
                                <option value="admin">Administrator</option>
                            </select>
                        </div>

                        <div className="h-px bg-gray-200" />

                        {/* Personal Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wide">Account Details</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email Address *</label>
                                <div className="mt-1">
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {isEditMode ? 'New Password (leave blank to keep current)' : 'Password *'}
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="password"
                                        required={!isEditMode}
                                        value={formData.password}
                                        onChange={(e) => {
                                            setFormData({ ...formData, password: e.target.value });
                                            if (isEditMode) setPasswordChanged(true);
                                        }}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="h-px bg-gray-200" />

                        {/* Contact & Company Details */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wide">Contact Information</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                                <div className="mt-1">
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            {(formData.role === 'client' || formData.role === 'vendor') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            value={formData.company}
                                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Address</label>
                                <div className="mt-1">
                                    <textarea
                                        rows={3}
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            {/* Vendor Specific */}
                            {formData.role === 'vendor' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Serviceable States</label>
                                    <MultiSelect
                                        options={INDIAN_STATES}
                                        value={formData.service_states || []}
                                        onChange={(vals) => setFormData({ ...formData, service_states: vals })}
                                        placeholder="Select states..."
                                    />
                                </div>
                            )}
                        </div>

                    </form>
                </div>

                {/* Footer Buttons - Fixed */}
                <div className="flex-shrink-0 px-4 py-4 flex justify-end bg-gray-50 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="user-form"
                        disabled={loading}
                        className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : 'Save User'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default UserDrawer;
